/**
 * Announcement Bar – Inline Countdown Timer
 *
 * Custom element: <announcement-countdown>
 *
 * Attributes
 *   data-countdown-type   "date" | "repeat" | "session"
 *   data-end-date         ISO-ish string, e.g. "2026-09-15 23:59"
 *   data-end-offset       Store UTC offset, e.g. "+0300". When present the
 *                         end date is read in store time, so the campaign
 *                         ends at the same moment for every visitor.
 *   data-repeat-hours     Number of hours for repeating cycle
 *   data-session-hours    Number of hours counted down on every new visit
 *   data-session-key      Storage key, shared by timers that must stay in sync
 */
if (!customElements.get('announcement-countdown')) {
  class AnnouncementCountdown extends HTMLElement {
    constructor() {
      super();
      this.daysEl = this.querySelector('[data-cd-days]');
      this.daysPartEl = this.querySelector('[data-cd-days-part]');
      this.hoursEl = this.querySelector('[data-cd-hours]');
      this.minutesEl = this.querySelector('[data-cd-minutes]');
      this.secondsEl = this.querySelector('[data-cd-seconds]');
      this.type = this.getAttribute('data-countdown-type') || 'date';
      this.interval = null;
    }

    connectedCallback() {
      this.calculateEndDate();
      if (!this.endDate || isNaN(this.endDate)) return;
      this.update();
      this.interval = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      if (this.interval) clearInterval(this.interval);
    }

    calculateEndDate() {
      if (this.type === 'date') {
        const raw = this.getAttribute('data-end-date');
        if (!raw) return;
        // Normalise "YYYY-MM-DD HH:MM" → "YYYY-MM-DDTHH:MM:00"
        let value = raw.trim().replace(' ', 'T');
        if (value.split(':').length < 3) value += ':00';

        // Without an offset the date is read in the visitor's own timezone,
        // which would end the campaign at a different moment abroad.
        const offset = (this.getAttribute('data-end-offset') || '').trim();
        if (/^[+-]\d{4}$/.test(offset)) {
          value += offset.slice(0, 3) + ':' + offset.slice(3);
        }

        this.endDate = Date.parse(value);
      } else if (this.type === 'repeat') {
        const hours = parseInt(this.getAttribute('data-repeat-hours'), 10) || 24;
        this.repeatMs = hours * 3600 * 1000;
        this.setRepeatEnd();
      } else if (this.type === 'session') {
        this.sessionHours = parseFloat(this.getAttribute('data-session-hours')) || 12;
        this.sessionKey = this.getAttribute('data-session-key') || 'campaign';
        this.setSessionEnd();
      }
    }

    /**
     * Per-visit mode: the timer starts over at its full length on every new
     * visit, and the shared helper keeps it ticking across page views.
     */
    setSessionEnd() {
      if (typeof window.sessionCountdownEnd === 'function') {
        this.endDate = window.sessionCountdownEnd(this.sessionKey, this.sessionHours);
      } else {
        this.endDate = Date.now() + this.sessionHours * 3600 * 1000;
      }
    }

    /**
     * For repeating mode we anchor to midnight of today and then find the
     * *next* cycle boundary from now.
     * Example: repeat = 6 h → cycles at 00:00, 06:00, 12:00, 18:00, 00:00…
     */
    setRepeatEnd() {
      const now = Date.now();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const anchor = today.getTime();

      const elapsed = (now - anchor) % this.repeatMs;
      this.endDate = now + (this.repeatMs - elapsed);
    }

    update() {
      let diff = this.endDate - Date.now();

      if (diff <= 0) {
        if (this.type === 'repeat' || this.type === 'session') {
          if (this.type === 'repeat') {
            this.setRepeatEnd();
          } else {
            this.setSessionEnd();
          }
          diff = this.endDate - Date.now();
        } else {
          // One-time countdown expired
          this.render(0, 0, 0, 0);
          clearInterval(this.interval);
          if (this.getAttribute('data-hide-on-complete') === 'true') this.hidden = true;
          return;
        }
      }

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.render(days, hours, minutes, seconds);
    }

    pad(n) {
      return n < 10 ? '0' + n : String(n);
    }

    /**
     * Days are hidden entirely on the last day so the bar never reads
     * "0 gün 5 saat 03 dakika 22 saniye".
     */
    render(d, h, m, s) {
      if (this.daysEl) this.daysEl.textContent = d;
      if (this.daysPartEl) this.daysPartEl.hidden = d === 0;
      if (this.hoursEl) this.hoursEl.textContent = this.pad(h);
      if (this.minutesEl) this.minutesEl.textContent = this.pad(m);
      if (this.secondsEl) this.secondsEl.textContent = this.pad(s);
    }
  }

  customElements.define('announcement-countdown', AnnouncementCountdown);
}

/**
 * Countdown Bar – height reporter
 *
 * The bar sits above the announcement bar and sticks to the top with it, so
 * everything else that pins below the announcement bar (the header, the filter
 * bars, the quick search panel) has to be pushed down by this bar's height.
 * The measurement is published as --countdown-bar-height on <html>.
 */
if (!customElements.get('countdown-bar')) {
  class CountdownBar extends HTMLElement {
    connectedCallback() {
      this.setHeight();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.setHeight());
        this.resizeObserver.observe(this);
      }

      this.onResize = () => this.setHeight();
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.onResize);
      // A removed bar must not keep pushing the header down.
      document.documentElement.style.setProperty('--countdown-bar-height', '0px');
    }

    setHeight() {
      const height = this.offsetHeight;
      if (height === this.lastHeight) return;
      this.lastHeight = height;
      document.documentElement.style.setProperty('--countdown-bar-height', height + 'px');
    }
  }

  customElements.define('countdown-bar', CountdownBar);
}
